import React, { useState, useContext } from 'react';
import {
  Box,
  Button,
  Typography,
  Grid,
  Paper,
  Divider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  TextField,
  Tooltip,
} from '@mui/material';
import { PayPalButtons } from '@paypal/react-paypal-js';
import { useNavigate, Link } from 'react-router-dom';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { useMutation, useQuery } from '@apollo/client';
import { UserContext } from '../../components/auth/auth-context';
import { useSelectedBusiness } from '../../redux/store';
import { BUY_COINS_MUTATION } from '../../graphql/mutations/tip';
import {
  GET_COIN_BALANCE,
  GET_WALLET_SUMMARY,
  GET_COIN_TRANSACTIONS,
} from '../../graphql/queries/tip';

const COIN_PRICE = 0.10;
const coinPacks = [
  { coins: 2000, price: 100.0 },
  { coins: 1000, price: 85.0 },
  { coins: 500, price: 45.0 },
  { coins: 250, price: 24.0 },
  { coins: 100, price: 10.0 },
  { coins: 50, price: 5.0 },
  { coins: 20, price: 2.0 },
  { coins: 10, price: 1.0 },
];

export const BuyCoinsPage = () => {
  const { providerUser } = useContext(UserContext);
  const [selectedPack, setSelectedPack] = useState(null);
  const [paypalOpen, setPaypalOpen] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [buyCoins, { loading: buying }] = useMutation(BUY_COINS_MUTATION, {
    // after you buy, refresh all of the dashboard’s data
    refetchQueries: [
      { query: GET_COIN_BALANCE, variables: { userId: providerUser?.userId } },
      { query: GET_WALLET_SUMMARY, variables: { userId: providerUser?.userId } },
      { query: GET_COIN_TRANSACTIONS, variables: { userId: providerUser?.userId } },
    ],
    awaitRefetchQueries: true,
  });
  const selectedBusiness = useSelectedBusiness();
  const navigate = useNavigate();

  // Custom amount state
  const [customCoins, setCustomCoins] = useState('');

  // Fetch current wallet balance
  const { data: walletData, loading: walletLoading } = useQuery(
    GET_COIN_BALANCE,
    { variables: { userId: providerUser?.userId }, skip: !providerUser?.userId }
  );
  const currentBalance = walletData?.getPlanyCoinWallet?.balance ?? 0;

  // Compute paths
  const biz = selectedBusiness?.businessBasic?.url;
  const staff = providerUser?.url;
  const walletPath = biz && staff ? `/${biz}/${staff}/wallet` : '/login';

  // Handlers
  const handleBuyClick = (pack) => {
    setSelectedPack(pack);
    setPaypalOpen(true);
  };
  const handlePaypalClose = () => {
    setPaypalOpen(false);
    setSelectedPack(null);
  };
  const handleSuccessClose = () => {
    setSuccessOpen(false);
    navigate(walletPath);
  };

  // PayPal approval
  const onApprove = async (_data, actions) => {
    await actions.order.capture();
    try {
      await buyCoins({
        variables: {
          input: {
            amount: selectedPack.coins,
            usdValue: selectedPack.price,
          },
        },
      });
      setPaypalOpen(false);
      setSuccessOpen(true);
    } catch (err) {
      console.error(err);
      alert('❌ Error crediting your wallet.');
      handlePaypalClose();
    }
  };

  // Compute custom price and validation
  const coinQuantity = Number(customCoins);
  const customPrice = Number((coinQuantity * COIN_PRICE).toFixed(2));
  const customInvalid = isNaN(coinQuantity) || coinQuantity < 10;

  return (
    <Box p={3}>
      {/* Back Link */}
      <Button
        component={Link}
        to={walletPath}
        startIcon={<ArrowBackIosIcon />}
        sx={{ mb: 2 }}
      >
        Back to Wallet
      </Button>

      <Typography variant="h5" gutterBottom>🛒 Buy PlanyCoins</Typography>

      {/* Current Balance */}
      {walletLoading ? (
        <CircularProgress size={24} />
      ) : (
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          💰 Current Balance: {currentBalance} PlanyCoins
        </Typography>
      )}

      {/* Instruction */}
      <Typography variant="body1" sx={{ mb: 2 }}>
        Select a pack, or enter a custom amount (min 10 coins), then pay with PayPal.
      </Typography>

      {/* Warning */}
      <Alert severity="warning" sx={{ mb: 3 }}>
        Purchased coins are for tipping others. If you cash out instead of tipping, you’ll forfeit the value you paid.
      </Alert>

      {/* Custom input */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <TextField
          label="Custom Coins"
          type="number"
          value={customCoins}
          onChange={(e) => setCustomCoins(e.target.value)}
          inputProps={{ min: 10 }}
        />
        <Tooltip
          title={customInvalid ? 'Please enter at least 10 coins' : ''}
          disableHoverListener={!customInvalid}
        >
          <span>
            <Button
              variant="outlined"
              disabled={buying || customInvalid}
              onClick={() => handleBuyClick({ coins: coinQuantity, price: customPrice })}
            >
              {buying && selectedPack?.coins === coinQuantity ? (
                <CircularProgress size={20} />
              ) : (
                `Buy ${customCoins || ''} for $${customPrice}`
              )}
            </Button>
          </span>
        </Tooltip>
      </Box>

      {/* Predefined packs */}
      <Grid container spacing={3}>
        {coinPacks.map((pack) => {
          const savings = (pack.coins * COIN_PRICE - pack.price).toFixed(2);
          return (
            <Grid item xs={12} sm={6} md={3} key={pack.coins}>
              <Paper elevation={2} sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h6">🪙 {pack.coins} Coins</Typography>
                <Typography variant="subtitle1">${pack.price.toFixed(2)}</Typography>
                <Divider sx={{ my: 1 }} />
                <Typography variant="caption" color="textSecondary">
                  You save ${savings}
                </Typography>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  sx={{ mt: 2 }}
                  onClick={() => handleBuyClick(pack)}
                  disabled={buying}
                >
                  {buying && selectedPack?.coins === pack.coins ? (
                    <CircularProgress size={20} />
                  ) : (
                    'Buy Now'
                  )}
                </Button>
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      {/* PayPal dialog */}
      <Dialog open={paypalOpen} onClose={handlePaypalClose} maxWidth="xs" fullWidth>
        <DialogTitle>
          Pay ${selectedPack?.price.toFixed(2)} for {selectedPack?.coins} Coins
        </DialogTitle>
        <DialogContent>
          {selectedPack && (
            <PayPalButtons
              style={{ layout: 'vertical' }}
              createOrder={(data, actions) =>
                actions.order.create({
                  purchase_units: [
                    {
                      amount: { value: selectedPack.price.toFixed(2) },
                      description: `Buy ${selectedPack.coins} PlanyCoins`,
                    },
                  ],
                })
              }
              onApprove={onApprove}
              onCancel={handlePaypalClose}
              onError={(err) => {
                console.error(err);
                alert('❌ PayPal error, please try again.');
                handlePaypalClose();
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePaypalClose}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Success confirmation */}
      <Dialog open={successOpen} onClose={handleSuccessClose} maxWidth="xs" fullWidth>
        <DialogTitle>Purchase Successful!</DialogTitle>
        <DialogContent>
          <Typography>
            You’ve successfully purchased {selectedPack?.coins} PlanyCoins.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSuccessClose} variant="contained" color="primary">
            Go to Wallet
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
